import {
  type AlternateLink,
  LOCALE_TO_PREFIX,
  normalizePath,
  extractAlternateLinksFromPageData,
} from "./alternateLinks";
import { getPageQueryForRoute, isPageNotFoundForRoute } from "./routeQueries";
import { fetchGlobalData } from "./utils";

function extractPageRecordFromResponse(pageData: unknown): unknown | undefined {
  if (!pageData || typeof pageData !== "object") {
    return undefined;
  }

  const record = pageData as { data?: unknown };
  if (Array.isArray(record.data)) {
    return record.data[0];
  }

  return record.data;
}

export const INTERNATIONAL_LOCALES = [
  "en",
  "pl",
  "ro",
  "hr",
  "el",
  "bg",
  "lt",
  "it",
  "hu",
] as const;

export const UKRAINE_NEWS_LOCALES = ["uk", "en"] as const;

export interface DiscoveryProbe {
  locale: string;
  query: string;
  url: string;
}

type LocalizationEntry = {
  locale?: string;
  slug?: string;
  publishedAt?: string | null;
};

function isPublishedEntry(entry: LocalizationEntry): boolean {
  if (!("publishedAt" in entry)) {
    return true;
  }

  return entry.publishedAt !== null;
}

function extractLocalizationEntries(record: unknown): LocalizationEntry[] {
  if (!record || typeof record !== "object") {
    return [];
  }

  const page = record as LocalizationEntry & {
    localizations?: LocalizationEntry[] | { data?: LocalizationEntry[] };
  };

  const entries: LocalizationEntry[] = [];

  if (page.locale && page.slug) {
    entries.push(page);
  }

  const localizations = page.localizations;
  if (Array.isArray(localizations)) {
    entries.push(...localizations);
  } else if (
    localizations &&
    typeof localizations === "object" &&
    Array.isArray(localizations.data)
  ) {
    entries.push(...localizations.data);
  }

  return entries;
}

function intlLocalesExcept(current?: string): string[] {
  return INTERNATIONAL_LOCALES.filter((locale) => locale !== current);
}

function withLocalizationsPopulate(query: string): string {
  if (
    query.includes("populate[localizations]") ||
    query.includes("customPopulate=nested")
  ) {
    return query;
  }
  const separator = query.includes("?") ? "&" : "?";
  return `${query}${separator}populate[localizations][fields][0]=slug&populate[localizations][fields][1]=locale&populate[localizations][fields][2]=publishedAt`;
}

function getAllowedLocalesForPath(pathname: string): readonly string[] {
  const path = normalizePath(pathname);

  if (path.startsWith("/ua/")) {
    return ["uk", ...INTERNATIONAL_LOCALES];
  }

  return INTERNATIONAL_LOCALES;
}

function getApiBaseFromPageQuery(pageQuery: string): string | null {
  return pageQuery.match(/^(\/api\/[^?]+)/)?.[1] ?? null;
}

function appendPublicationParams(pageQuery: string, query: string): string {
  if (pageQuery.includes("status=preview")) {
    return `${query}&status=preview`;
  }

  return query;
}

function encodeFilterSlug(slug: string): string {
  return encodeURIComponent(slug);
}

function homepageProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "en",
      query: "/api/homepage?customPopulate=nested",
      url: "/",
    },
    ...intlLocalesExcept("en").map((locale) => ({
      locale,
      query: `/api/homepage-ukraine?customPopulate=nested&locale=${locale}`,
      url: LOCALE_TO_PREFIX[locale],
    })),
  ];
}

function ukraineHomepageProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "uk",
      query: "/api/homepage-ukraine?customPopulate=nested&locale=uk",
      url: "/ua",
    },
    {
      locale: "en",
      query: "/api/homepage-ukraine?customPopulate=nested&locale=en",
      url: "/en",
    },
  ];
}

function internationalNewsListProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "en",
      query: "/api/news-international?customPopulate=nested",
      url: "/news",
    },
    ...intlLocalesExcept("en").map((locale) => ({
      locale,
      query: `/api/news-international?customPopulate=nested&locale=${locale}`,
      url: `${LOCALE_TO_PREFIX[locale]}/news`,
    })),
  ];
}

function ukraineNewsListProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "en",
      query: "/api/news-ukraine?customPopulate=nested&locale=en",
      url: "/en/news",
    },
    {
      locale: "uk",
      query: "/api/news-ukraine?customPopulate=nested&locale=uk",
      url: "/ua/news",
    },
  ];
}

function internationalEsgProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "en",
      query: "/api/esg-international?customPopulate=nested&locale=en",
      url: "/esg",
    },
    ...intlLocalesExcept("en").map((locale) => ({
      locale,
      query: `/api/esg-ukraine?customPopulate=nested&locale=${locale}`,
      url: `${LOCALE_TO_PREFIX[locale]}/esg`,
    })),
  ];
}

function internationalRegulatoryProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "en",
      query: "/api/regulatory?customPopulate=nested",
      url: "/regulatory",
    },
    ...intlLocalesExcept("en").map((locale) => ({
      locale,
      query: `/api/regulatory-ukraine?customPopulate=nested&locale=${locale}`,
      url: `${LOCALE_TO_PREFIX[locale]}/regulatory`,
    })),
  ];
}

function internationalCareersProbes(): DiscoveryProbe[] {
  return [
    {
      locale: "en",
      query: "/api/career?customPopulate=nested",
      url: "/careers",
    },
    ...intlLocalesExcept("en").map((locale) => ({
      locale,
      query: `/api/career-ukraine?customPopulate=nested&locale=${locale}`,
      url: `${LOCALE_TO_PREFIX[locale]}/careers`,
    })),
  ];
}

export function getDiscoveryProbes(pathname: string): DiscoveryProbe[] {
  const path = normalizePath(pathname);

  if (path === "/") {
    return homepageProbes();
  }

  if (path.match(/^\/(pl|ro|hr|el|bg|lt|it|hu)$/)) {
    return homepageProbes();
  }

  if (path === "/ua" || path === "/en") {
    return ukraineHomepageProbes();
  }

  if (path === "/news") {
    return internationalNewsListProbes();
  }

  if (path === "/en/news" || path === "/ua/news") {
    return ukraineNewsListProbes();
  }

  if (path.match(/^\/(pl|ro|hr|el|bg)\/news$/)) {
    return internationalNewsListProbes();
  }

  if (path === "/esg" || path === "/esg_test") {
    return internationalEsgProbes();
  }

  if (path.match(/^\/(pl|ro|hr|el|bg)\/esg(_test)?$/)) {
    return internationalEsgProbes().map((probe) => ({
      ...probe,
      url:
        probe.locale === "en"
          ? "/esg"
          : `${LOCALE_TO_PREFIX[probe.locale]}/esg`,
    }));
  }

  if (path.match(/^\/(ua|en)\/esg(_test)?$/)) {
    const suffix = path.includes("esg_test") ? "esg_test" : "esg";
    return [
      {
        locale: "en",
        query: `/api/esg-ukraine?customPopulate=nested&locale=en`,
        url: `/en/${suffix}`,
      },
      {
        locale: "uk",
        query: `/api/esg-ukraine?customPopulate=nested&locale=uk`,
        url: `/ua/${suffix}`,
      },
    ];
  }

  if (path === "/regulatory") {
    return internationalRegulatoryProbes();
  }

  if (path.match(/^\/(pl|ro|hr|el|bg)\/regulatory$/)) {
    return internationalRegulatoryProbes();
  }

  if (path.match(/^\/(ua|en)\/regulatory$/)) {
    return [
      {
        locale: "en",
        query: "/api/regulatory-ukraine?customPopulate=nested&locale=en",
        url: "/en/regulatory",
      },
      {
        locale: "uk",
        query: "/api/regulatory-ukraine?customPopulate=nested&locale=uk",
        url: "/ua/regulatory",
      },
    ];
  }

  if (path === "/careers") {
    return internationalCareersProbes();
  }

  if (path.match(/^\/(pl|ro|hr|el|bg)\/careers$/)) {
    return internationalCareersProbes();
  }

  const dynamicPageMatch = path.match(
    /^\/(?:ua|en|pl|ro|hr|el|bg|lt|it|hu)\/page\/([^/]+)$/,
  );
  const dynamicPageNoPrefixMatch =
    !dynamicPageMatch && path.match(/^\/page\/([^/]+)$/);
  if (dynamicPageMatch || dynamicPageNoPrefixMatch) {
    const slug = dynamicPageMatch
      ? dynamicPageMatch[1]
      : (dynamicPageNoPrefixMatch as RegExpMatchArray)[1];
    const hasUaPrefix = dynamicPageMatch && path.startsWith("/ua/");
    const locales = hasUaPrefix
      ? (["uk", ...INTERNATIONAL_LOCALES] as const)
      : INTERNATIONAL_LOCALES;

    return locales.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        `/api/esg-ukraines?filters[slug][$eq]=${encodeFilterSlug(slug)}&customPopulate=nested&locale=${locale}`,
      ),
      url:
        locale === "en"
          ? `/page/${slug}`
          : locale === "uk"
            ? `/ua/page/${slug}`
            : `${LOCALE_TO_PREFIX[locale]}/page/${slug}`,
    }));
  }

  const intlArticleMatch = path.match(/^\/news\/([^/]+)$/);
  if (intlArticleMatch) {
    const slug = intlArticleMatch[1];
    return INTERNATIONAL_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        locale === "en"
          ? `/api/articles?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}`
          : `/api/articles?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url:
        locale === "en"
          ? `/news/${slug}`
          : `${LOCALE_TO_PREFIX[locale]}/news/${slug}`,
    }));
  }

  const localizedArticleMatch = path.match(
    /^\/(pl|ro|hr|el|bg)\/news\/([^/]+)$/,
  );
  if (localizedArticleMatch) {
    const slug = localizedArticleMatch[2];
    return INTERNATIONAL_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        locale === "en"
          ? `/api/articles?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}`
          : `/api/articles?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url:
        locale === "en"
          ? `/news/${slug}`
          : `${LOCALE_TO_PREFIX[locale]}/news/${slug}`,
    }));
  }

  const ukraineArticleMatch = path.match(/^\/(ua|en)\/news\/([^/]+)$/);
  if (ukraineArticleMatch) {
    const slug = ukraineArticleMatch[2];
    return UKRAINE_NEWS_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        `/api/news-for-ukraines?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url: locale === "en" ? `/en/news/${slug}` : `/ua/news/${slug}`,
    }));
  }

  const intlProductMatch = path.match(/^\/products\/([^/]+)$/);
  if (intlProductMatch) {
    const slug = intlProductMatch[1];
    return INTERNATIONAL_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        locale === "en"
          ? `/api/product-internationals?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}`
          : `/api/product-internationals?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url:
        locale === "en"
          ? `/products/${slug}`
          : `${LOCALE_TO_PREFIX[locale]}/products/${slug}`,
    }));
  }

  const localizedProductMatch = path.match(
    /^\/(ua|en|pl|ro|hr|el|bg)\/products\/([^/]+)$/,
  );
  if (localizedProductMatch) {
    const slug = localizedProductMatch[2];
    return INTERNATIONAL_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        `/api/product-ukraines?customPopulate=nested&status=preview&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url:
        locale === "en"
          ? `/products/${slug}`
          : `${LOCALE_TO_PREFIX[locale]}/products/${slug}`,
    })).filter((probe) => probe.locale !== "uk");
  }

  const intlPageSlugMatch = path.match(/^\/([^/]+)$/);
  if (
    intlPageSlugMatch &&
    !["news", "products", "esg", "esg_test", "regulatory", "careers"].includes(
      intlPageSlugMatch[1],
    )
  ) {
    const slug = intlPageSlugMatch[1];
    return INTERNATIONAL_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        locale === "en"
          ? `/api/pages?customPopulate=nested&filters[slug][$eq]=${encodeFilterSlug(slug)}`
          : `/api/pages?customPopulate=nested&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url: locale === "en" ? `/${slug}` : `${LOCALE_TO_PREFIX[locale]}/${slug}`,
    }));
  }

  const localizedPageSlugMatch = path.match(
    /^\/(ua|en|pl|ro|hr|el|bg|lt|it|hu)\/([^/]+)$/,
  );
  if (
    localizedPageSlugMatch &&
    ![
      "news",
      "products",
      "esg",
      "esg_test",
      "regulatory",
      "regulatory-dtrading",
      "regulatory_tmp",
      "careers",
      "careers-dtrading",
      "info",
      "page",
    ].includes(localizedPageSlugMatch[2])
  ) {
    const slug = localizedPageSlugMatch[2];
    return INTERNATIONAL_LOCALES.map((locale) => ({
      locale,
      query: withLocalizationsPopulate(
        `/api/pages?customPopulate=nested&filters[slug][$eq]=${encodeFilterSlug(slug)}&locale=${locale}`,
      ),
      url: locale === "en" ? `/${slug}` : `${LOCALE_TO_PREFIX[locale]}/${slug}`,
    }));
  }

  return [];
}

export function rebuildCollectionUrl(
  pathname: string,
  locale: string,
  slug: string,
): string {
  const path = normalizePath(pathname);

  if (path.includes("/page/")) {
    return locale === "en"
      ? `/page/${slug}`
      : `${LOCALE_TO_PREFIX[locale]}/page/${slug}`;
  }

  if (path.includes("/news/")) {
    if (path.startsWith("/ua/") || path.startsWith("/en/")) {
      return locale === "en" ? `/en/news/${slug}` : `/ua/news/${slug}`;
    }
    return locale === "en"
      ? `/news/${slug}`
      : `${LOCALE_TO_PREFIX[locale]}/news/${slug}`;
  }

  if (path.includes("/products/")) {
    return locale === "en"
      ? `/products/${slug}`
      : `${LOCALE_TO_PREFIX[locale]}/products/${slug}`;
  }

  return locale === "en" ? `/${slug}` : `${LOCALE_TO_PREFIX[locale]}/${slug}`;
}

export function buildAlternateLinksFromLocalizations(
  record: unknown,
  pathname: string,
  allowedLocales: readonly string[] = getAllowedLocalesForPath(pathname),
): AlternateLink[] {
  const entries = extractLocalizationEntries(record);
  const links: AlternateLink[] = [];
  const seen = new Set<string>();

  for (const entry of entries) {
    if (!entry.locale || !entry.slug) {
      continue;
    }
    if (!allowedLocales.includes(entry.locale)) {
      continue;
    }
    if (!isPublishedEntry(entry)) {
      continue;
    }

    const url = rebuildCollectionUrl(pathname, entry.locale, entry.slug);
    if (seen.has(entry.locale)) {
      continue;
    }

    links.push({ locale: entry.locale, url: normalizePath(url) });
    seen.add(entry.locale);
  }

  return links;
}

export async function discoverAlternateLinksByDocumentId(
  record: LocalizationEntry & { documentId?: string },
  pathname: string,
  pageQuery: string,
  fetchFn: (url: string) => Promise<unknown>,
): Promise<AlternateLink[]> {
  const documentId = record.documentId;
  if (!documentId) {
    return [];
  }

  const apiBase = getApiBaseFromPageQuery(pageQuery);
  if (!apiBase) {
    return [];
  }

  const path = normalizePath(pathname);
  if (
    !path.includes("/page/") &&
    !path.includes("/news/") &&
    !path.includes("/products/")
  ) {
    return [];
  }

  const allowedLocales = getAllowedLocalesForPath(pathname);

  const results = await Promise.all(
    allowedLocales.map(async (locale) => {
      const query = appendPublicationParams(
        pageQuery,
        `${apiBase}?filters[documentId][$eq]=${encodeURIComponent(documentId)}&locale=${locale}&customPopulate=nested&fields[0]=slug&fields[1]=locale&fields[2]=publishedAt&fields[3]=documentId`,
      );

      try {
        const response = await fetchFn(query);
        if (isPageNotFoundForRoute(response, query)) {
          return null;
        }

        const item = extractPageRecordFromResponse(response) as
          | LocalizationEntry
          | undefined;
        if (!item?.locale || !item.slug) {
          return null;
        }
        if (!allowedLocales.includes(item.locale)) {
          return null;
        }

        return {
          locale: item.locale,
          url: normalizePath(
            rebuildCollectionUrl(pathname, item.locale, item.slug),
          ),
        };
      } catch {
        return null;
      }
    }),
  );

  return results.filter((link): link is AlternateLink => link !== null);
}

export async function discoverAlternateLinksByProbe(
  pathname: string,
  fetchFn: (url: string) => Promise<unknown>,
): Promise<AlternateLink[]> {
  const probes = getDiscoveryProbes(pathname);
  if (!probes.length) {
    return [];
  }

  const results = await Promise.all(
    probes.map(async (probe) => {
      try {
        const response = await fetchFn(probe.query);
        if (isPageNotFoundForRoute(response, probe.query)) {
          return null;
        }

        const record = extractPageRecordFromResponse(response) as
          | { slug?: string }
          | undefined;
        const url =
          record?.slug && pathname.match(/\/(page|news|products)\//)
            ? rebuildCollectionUrl(pathname, probe.locale, record.slug)
            : probe.url;

        return { locale: probe.locale, url: normalizePath(url) };
      } catch {
        return null;
      }
    }),
  );

  return results.filter((link): link is AlternateLink => link !== null);
}

export async function resolveAlternateLinks(
  pathname: string,
  pageData: unknown | undefined,
  fetchFn: (url: string) => Promise<unknown>,
): Promise<AlternateLink[] | undefined> {
  let record = pageData
    ? (extractPageRecordFromResponse(pageData) as
        | (LocalizationEntry & { documentId?: string })
        | undefined)
    : undefined;

  let links = record
    ? buildAlternateLinksFromLocalizations(record, pathname)
    : [];

  const pageQuery = getPageQueryForRoute(pathname);
  if (links.length <= 1 && pageQuery) {
    try {
      const enriched = await fetchFn(withLocalizationsPopulate(pageQuery));
      const enrichedRecord = extractPageRecordFromResponse(enriched) as
        | (LocalizationEntry & { documentId?: string })
        | undefined;
      if (enrichedRecord) {
        record = enrichedRecord;
      }
      const enrichedLinks = buildAlternateLinksFromLocalizations(
        enrichedRecord,
        pathname,
      );
      if (enrichedLinks.length > links.length) {
        links = enrichedLinks;
      }
    } catch {
      // fall through to documentId / probe discovery
    }
  }

  if (links.length <= 1 && record && pageQuery) {
    const byDocumentId = await discoverAlternateLinksByDocumentId(
      record,
      pathname,
      pageQuery,
      fetchFn,
    );
    if (byDocumentId.length > links.length) {
      const merged = new Map<string, AlternateLink>();
      for (const link of [...links, ...byDocumentId]) {
        merged.set(link.locale, link);
      }
      links = Array.from(merged.values());
    }
  }

  if (links.length <= 1) {
    const probed = await discoverAlternateLinksByProbe(pathname, fetchFn);
    if (probed.length) {
      const merged = new Map<string, AlternateLink>();
      for (const link of [...links, ...probed]) {
        merged.set(link.locale, link);
      }
      links = Array.from(merged.values());
    }
  }

  return links.length ? links : undefined;
}

export async function resolveAlternateLinksForRoute(
  pathname: string,
  pageData?: unknown,
  fetchFn: (url: string) => Promise<unknown> = fetchGlobalData,
): Promise<AlternateLink[] | undefined> {
  const record = pageData ? extractPageRecordFromResponse(pageData) : undefined;
  const cmsLinks = extractAlternateLinksFromPageData(record);
  if (cmsLinks?.length) {
    return cmsLinks;
  }

  return resolveAlternateLinks(pathname, pageData, fetchFn);
}

/** @deprecated Use resolveAlternateLinksForRoute */
export async function fetchAlternateLinksForRoute(
  pathname: string,
  fetchFn: (url: string) => Promise<unknown> = fetchGlobalData,
): Promise<AlternateLink[] | undefined> {
  return resolveAlternateLinksForRoute(pathname, undefined, fetchFn);
}
